import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", color: "#6366F1", description: "", active: true };

export default function ProjectTypes() {
  const qc = useQueryClient();
  const { can } = useApp();
  const [modal, setModal] = useState(null); // { mode: "new"|"edit", data }

  const { data: types, isLoading } = useQuery({ queryKey: ["project-types"], queryFn: () => get("/crm/project-types") });

  const create = useMutation({
    mutationFn: (body) => post("/crm/project-types", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["project-types"] }); toast.success("Project type created"); setModal(null); },
  });
  const update = useMutation({
    mutationFn: ({ id, body }) => patch(`/crm/project-types/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["project-types"] }); toast.success("Project type updated"); setModal(null); },
  });
  const remove = useMutation({
    mutationFn: (id) => del(`/crm/project-types/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["project-types"] }); toast.success("Project type deleted"); },
    onError: () => toast.error("Can't delete — pipelines still use this type"),
  });

  return (
    <div>
      <PageHeader title="Project Types" subtitle="Drive pipeline selection on the Leads page.">
        {can("crm", "edit") && (
          <Button data-testid="new-project-type-btn" onClick={() => setModal({ mode: "new", data: EMPTY })}>
            <Plus className="mr-1.5 h-4 w-4" />New Project Type
          </Button>
        )}
      </PageHeader>

      {isLoading ? <Skeleton className="h-64" /> : !types?.length ? (
        <EmptyState icon={Layers} title="No project types yet" subtitle="Create one to start organizing pipelines by type." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="project-types-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Color</th>
                <th className="px-4 py-3 font-semibold">Pipelines</th>
                <th className="px-4 py-3 font-semibold">Active</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id} data-testid={`project-type-row-${t.id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium text-foreground">{t.name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: t.color }} />
                      <span className="text-xs text-muted-foreground">{t.color}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{t.pipeline_count}</td>
                  <td className="px-4 py-3">
                    {can("crm", "edit") ? (
                      <Switch checked={t.active} data-testid={`active-toggle-${t.id}`}
                        onCheckedChange={(v) => update.mutate({ id: t.id, body: { active: v } })} />
                    ) : (
                      <span className="text-xs text-muted-foreground">{t.active ? "Active" : "Inactive"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {can("crm", "edit") && (
                        <Button variant="ghost" size="icon" data-testid={`edit-project-type-${t.id}`}
                          onClick={() => setModal({ mode: "edit", data: t })}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {can("crm", "delete") && (
                        <ConfirmDelete testid={`del-project-type-${t.id}`} title={`Delete "${t.name}"?`}
                          description="Blocked if any pipeline still uses this type."
                          trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                          onConfirm={() => remove.mutate(t.id)} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ProjectTypeModal state={modal} setState={setModal}
        onSubmit={(body) => modal?.mode === "new" ? create.mutate(body) : update.mutate({ id: modal.data.id, body })} />
    </div>
  );
}

function ProjectTypeModal({ state, setState, onSubmit }) {
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm(state?.data || EMPTY); }}
      key={state?.data?.id || "new"}>
      <DialogContent className="max-w-md" data-testid="project-type-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Project Type" : "New Project Type"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input data-testid="pt-name" className="mt-1" value={form.name ?? state?.data?.name ?? ""}
              onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <Label>Color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" data-testid="pt-color" className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent"
                value={form.color ?? state?.data?.color ?? "#6366F1"} onChange={(e) => set("color", e.target.value)} />
              <Input value={form.color ?? state?.data?.color ?? "#6366F1"} onChange={(e) => set("color", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea className="mt-1" value={form.description ?? state?.data?.description ?? ""}
              onChange={(e) => set("description", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="pt-submit" onClick={() => {
            if (!(form.name ?? state?.data?.name)) { toast.error("Name is required"); return; }
            onSubmit({
              name: form.name ?? state?.data?.name,
              color: form.color ?? state?.data?.color ?? "#6366F1",
              description: form.description ?? state?.data?.description ?? "",
            });
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
